import React, { useLayoutEffect, useRef } from 'react';
import NativeDateField from './common/NativeDateField';

export const DAILY_LOG_FLOW_STEPS = [
  { id: 'crew', label: 'Crew Timesheet' },
  { id: 'equipment', label: 'Equipment Time Card' },
  { id: 'production', label: 'Production Tracking' },
  { id: 'inventory', label: 'Inventory Tracking' },
];

export function DailyLogBreadcrumbs({ currentStep, onStepChange, disabled = false }) {
  const activeRef = useRef(null);

  useLayoutEffect(() => {
    const scrollActive = () => {
      activeRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    };
    scrollActive();
    const nav = activeRef.current?.closest('nav');
    if (!nav || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(scrollActive);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [currentStep]);

  return (
    <nav
      aria-label="Daily log screens"
      className="flex min-w-0 flex-1 items-center gap-x-1 sm:gap-x-1.5 overflow-x-auto whitespace-nowrap py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {DAILY_LOG_FLOW_STEPS.map((step, index) => {
        const active = step.id === currentStep;
        return (
          <React.Fragment key={step.id}>
            {index > 0 && (
              <span className="shrink-0 text-[13px] text-gray-400" aria-hidden="true">
                ›
              </span>
            )}
            <button
              type="button"
              ref={active ? activeRef : undefined}
              onClick={() => {
                if (!active && !disabled) onStepChange?.(step.id);
              }}
              aria-current={active ? 'page' : undefined}
              disabled={active || disabled}
              className={`shrink-0 rounded-md font-poppins leading-none touch-manipulation ${
                active
                  ? 'cursor-default bg-[#F8E8E9] px-2 py-1 text-[14px] sm:text-[17px] font-bold text-[#AD2226]'
                  : 'px-0.5 py-1 text-[12px] sm:text-[15px] font-medium text-[#424143] hover:text-[#AD2226] hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline'
              }`}
            >
              {step.label}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

const fieldLabelClass = 'text-[14px] font-normal text-[#424143] font-poppins';
const fieldInputClass =
  'h-10 w-full min-w-0 rounded-md border border-gray-300 px-3 text-sm text-[#2C2C2C] truncate focus:outline-none focus:ring-1 focus:ring-blue-500';

/** Project No. / Name / Work Date / Activity — same grid on every daily-log screen. */
export function DailyLogProjectFields({
  projectNo = '',
  projectName = '',
  workDate = '',
  onWorkDateChange,
  activity = '',
}) {
  return (
    <section className="nettles-daily-log-fields grid min-w-0 grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 px-4 md:px-6 pt-4 pb-1 bg-white">
      <div className="flex min-w-0 flex-col gap-1">
        <label className={fieldLabelClass}>Project No.</label>
        <input value={projectNo} readOnly className={fieldInputClass} />
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <label className={fieldLabelClass}>Project Name</label>
        <input
          value={projectName}
          readOnly
          title={projectName}
          className={fieldInputClass}
        />
      </div>
      <NativeDateField
        label="Work Date"
        labelClassName={fieldLabelClass}
        value={workDate}
        onChange={onWorkDateChange}
        size="md"
        className="min-w-0"
      />
      <div className="flex min-w-0 flex-col gap-1">
        <label className={fieldLabelClass}>Activity</label>
        <input
          value={activity}
          readOnly
          title={activity}
          className={fieldInputClass}
        />
      </div>
    </section>
  );
}

export function DailyLogBackButton({ currentStep, onStepChange, disabled = false }) {
  const index = DAILY_LOG_FLOW_STEPS.findIndex((step) => step.id === currentStep);
  const previous = index > 0 ? DAILY_LOG_FLOW_STEPS[index - 1] : null;

  return (
    <button
      type="button"
      onClick={() => {
        if (previous && !disabled) onStepChange?.(previous.id);
      }}
      disabled={!previous || disabled}
      className="h-12 sm:h-11 shrink-0 min-w-[5.75rem] sm:min-w-0 px-5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
    >
      Back
    </button>
  );
}
